import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Navigate } from 'react-router-dom';
import { Pencil, Trash2, X, Search, MapPin, Building2 } from 'lucide-react';
import { activitiesAPI, moderationAPI, usersAPI } from '../services/api';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import ActivityForm from '../components/Forms/ActivityForm';
import { Card } from '../components/Common/Card';
import { Button } from '../components/Common/Button';

// Nur die Felder, die die Tabelle anzeigt (Details kommen beim Bearbeiten nach).
const LIST_FIELDS = 'id,name,age_min,age_max,website,created_at';
// Umkreis um die geokodierte Filteradresse (entspricht den früheren 0.05 km)
const ADDRESS_RADIUS_M = 50;
const EMPTY_ADDRESS_FILTER = { text: '', coords: null };

const ActivityReview = () => {
  const { user, loading: authLoading } = useAuth();
  const isModerator = user?.role === 'moderator' || user?.role === 'admin';
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [addressFilter, setAddressFilter] = useState('');
  const [locationNameFilter, setLocationNameFilter] = useState('');
  const [editingActivity, setEditingActivity] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  // Adressfilter, nachdem das Geocoding abgeschlossen ist. Mit Koordinaten
  // filtert der Server per ST_DWithin, sonst per Textvergleich.
  const [appliedAddress, setAppliedAddress] = useState(EMPTY_ADDRESS_FILTER);
  const [geocodingFilter, setGeocodingFilter] = useState(false);
  const itemsPerPage = 20;
  const editPanelRef = useRef(null);
  const debouncedSearch = useDebouncedValue(searchTerm);
  const debouncedLocationName = useDebouncedValue(locationNameFilter);

  const loadActivities = useCallback(async () => {
    setLoading(true);
    try {
      const response = await moderationAPI.getAll({
        q: debouncedSearch.trim() || undefined,
        location_name: debouncedLocationName.trim() || undefined,
        address: appliedAddress.coords ? undefined : appliedAddress.text || undefined,
        lat: appliedAddress.coords?.lat,
        lng: appliedAddress.coords?.lng,
        radius_m: appliedAddress.coords ? ADDRESS_RADIUS_M : undefined,
        sort: 'name',
        fields: LIST_FIELDS,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage,
      });
      const { items, total: totalCount } = response.data;
      setActivities(items);
      setTotal(totalCount);
      if (items.length === 0 && totalCount > 0 && currentPage > 1) {
        setCurrentPage(Math.max(1, Math.ceil(totalCount / itemsPerPage)));
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      alert('Fehler beim Laden der Aktivitäten: ' + error.message);
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [debouncedSearch, debouncedLocationName, appliedAddress, currentPage]);

  useEffect(() => {
    if (authLoading) return;
    if (!isModerator) {
      setLoading(false);
      setInitialLoading(false);
      return;
    }

    // Solange Eingaben noch entprellt bzw. geokodiert werden, nicht mit
    // veralteten Filterwerten laden
    if (
      searchTerm.trim() !== debouncedSearch.trim() ||
      locationNameFilter.trim() !== debouncedLocationName.trim() ||
      addressFilter.trim() !== appliedAddress.text
    ) {
      return;
    }

    loadActivities();
  }, [
    isModerator,
    authLoading,
    loadActivities,
    searchTerm,
    debouncedSearch,
    locationNameFilter,
    debouncedLocationName,
    addressFilter,
    appliedAddress,
  ]);

  useEffect(() => {
    if (editingActivity && editPanelRef.current) {
      editPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(() => {
        const firstInput = editPanelRef.current?.querySelector('input, textarea');
        if (firstInput) firstInput.focus();
      }, 500);
    }
  }, [editingActivity]);

  // Adresse geokodieren und erst danach als Filter übernehmen – so löst eine
  // Eingabe genau eine Listenabfrage aus.
  useEffect(() => {
    const value = addressFilter.trim();
    if (!value) {
      setAppliedAddress((prev) => (prev.text === '' && !prev.coords ? prev : EMPTY_ADDRESS_FILTER));
      setGeocodingFilter(false);
      return undefined;
    }
    setGeocodingFilter(true);
    const timer = setTimeout(async () => {
      try {
        const response = await usersAPI.geocode(value);
        const coords =
          response.data?.lat != null && response.data?.lng != null
            ? { lat: response.data.lat, lng: response.data.lng }
            : null;
        setAppliedAddress({ text: value, coords });
      } catch {
        setAppliedAddress({ text: value, coords: null });
      } finally {
        setGeocodingFilter(false);
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [addressFilter]);

  const handleReject = async (activityId) => {
    if (!window.confirm('Möchten Sie diese Aktivität ablehnen?')) return;

    const reason = window.prompt('Bitte geben Sie eine Begründung für die Ablehnung ein:');
    if (reason === null) return;
    if (!reason.trim()) {
      alert('Eine Begründung ist erforderlich.');
      return;
    }

    try {
      await moderationAPI.reject(activityId, reason.trim());
      loadActivities();
    } catch (error) {
      console.error('Error rejecting activity:', error);
      const apiMessage = error.response?.data?.error;
      alert(apiMessage || 'Fehler beim Ablehnen der Aktivität');
    }
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm('Möchten Sie diese Aktivität wirklich löschen?')) return;
    try {
      await activitiesAPI.delete(activityId);
      loadActivities();
    } catch (error) {
      console.error('Error deleting activity:', error);
      alert('Fehler beim Löschen der Aktivität');
    }
  };

  // Die Liste enthält nur Anzeigefelder – für das Formular den vollen Datensatz holen
  const handleEdit = async (activity) => {
    try {
      const response = await activitiesAPI.getById(activity.id);
      setEditingActivity(response.data);
    } catch (error) {
      console.error('Error loading activity:', error);
      alert('Fehler beim Laden der Aktivität');
    }
  };

  const resetPage = () => setCurrentPage(1);

  const handleEditSuccess = () => {
    setEditingActivity(null);
    loadActivities();
  };

  const handleEditCancel = () => {
    setEditingActivity(null);
  };

  if (authLoading || initialLoading) {
    return (
      <div className="moderation-page">
        <h1>Aktivitäten überprüfen</h1>
        <p>Lädt...</p>
      </div>
    );
  }

  if (!isModerator) {
    return <Navigate to="/" />;
  }

  // Filterung, Sortierung und Paginierung passieren serverseitig
  const paginatedActivities = activities;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="moderation-page">
      <h1>Aktivitäten überprüfen</h1>

      {editingActivity && (
        <div className="edit-activity-panel" ref={editPanelRef}>
          <div className="edit-activity-header">
            <h2>Aktivität bearbeiten</h2>
            <Button
              variant="outline"
              className="btn-sm"
              onClick={handleEditCancel}
              aria-label="Bearbeitung schließen"
              style={{ borderRadius: '9999px', padding: 'var(--spacing-2)' }}
            >
              <X size={18} />
            </Button>
          </div>
          <ActivityForm
            mode="edit"
            activityId={editingActivity.id}
            initialData={editingActivity}
            onSuccess={handleEditSuccess}
            onCancel={handleEditCancel}
          />
        </div>
      )}

      <div style={{
        display: 'flex',

        alignItems: 'flex-start',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        {/* Address filter */}
        <div style={{ position: 'relative', flex: '1 1 250px' }}>
          <input
            type="text"
            placeholder="Nach Adresse filtern..."
            value={addressFilter}
            onChange={(e) => { setAddressFilter(e.target.value); resetPage(); }}
            style={{
              padding: '8px 35px 8px 12px',
              border: '1px solid #ccc',
              borderRadius: 'var(--radius-sm)',
              width: '100%',
              fontSize: '14px'
            }}
          />
          {geocodingFilter ? (
            <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', color: '#6c757d' }}>…</span>
          ) : (
            <MapPin
              size={18}
              style={{
                position: 'absolute', right: '10px', top: '50%',
                transform: 'translateY(-50%)', color: appliedAddress.coords ? '#28a745' : '#6c757d', pointerEvents: 'none'
              }}
            />
          )}
        </div>

        {/* Location name filter */}
        <div style={{ position: 'relative', flex: '1 1 250px' }}>
          <input
            type="text"
            placeholder="Nach Ortsbezeichnung filtern..."
            value={locationNameFilter}
            onChange={(e) => { setLocationNameFilter(e.target.value); resetPage(); }}
            style={{
              padding: '8px 35px 8px 12px',
              border: '1px solid #ccc',
              borderRadius: 'var(--radius-sm)',
              width: '100%',
              fontSize: '14px'
            }}
          />
          <Building2
            size={18}
            style={{
              position: 'absolute', right: '10px', top: '50%',
              transform: 'translateY(-50%)', color: locationNameFilter.trim() ? '#28a745' : '#6c757d', pointerEvents: 'none'
            }}
          />
        </div>

        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 250px' }}>
          <input
            type="text"
            placeholder="Aktivitäten durchsuchen..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); resetPage(); }}
            style={{
              padding: '8px 35px 8px 12px',
              border: '1px solid #ccc',
              borderRadius: 'var(--radius-sm)',
              width: '100%',
              fontSize: '14px'
            }}
          />
          <Search
            size={18}
            style={{
              position: 'absolute', right: '10px', top: '50%',
              transform: 'translateY(-50%)', color: '#6c757d', pointerEvents: 'none'
            }}
          />
        </div>
      </div>

      {loading ? (
        <p>Lädt...</p>
      ) : total === 0 ? (
        <p>
          {addressFilter.trim() || searchTerm.trim() || locationNameFilter.trim()
            ? 'Keine Aktivitäten für die eingegebenen Filterkriterien gefunden.'
            : 'Keine Aktivitäten vorhanden.'}
        </p>
      ) : (
        <>
          {/* Desktop Table */}
          <table className="activities-table activities-table-desktop">
            <thead>
              <tr>
                <th>Name</th>
                <th>Alter</th>
                <th>Website</th>
                <th>Erstellt am</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {paginatedActivities.map(activity => (
                <tr key={activity.id}>
                  <td>{activity.name}</td>
                  <td>{activity.age_min} - {activity.age_max} Jahre</td>
                  <td>
                    {activity.website ? (
                      <a href={activity.website} target="_blank" rel="noopener noreferrer">
                        Link
                      </a>
                    ) : '-'}
                  </td>
                  <td>{new Date(activity.created_at).toLocaleDateString('de-DE')}</td>
                  <td className="action-buttons" style={{ padding: 0 }}>
                    <Button
                      variant="outline"
                      className="btn-sm btn-reject"
                      onClick={() => handleReject(activity.id)}
                      aria-label="Ablehnen"
                      title="Ablehnen"
                      style={{ flex: 1 }}
                    >
                      <X size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      className="btn-sm btn-delete"
                      onClick={() => handleDelete(activity.id)}
                      aria-label="Löschen"
                      title="Löschen"
                      style={{ flex: 1 }}
                    >
                      <Trash2 size={16} />
                    </Button>
                    <Button
                      variant="outline"
                      className="btn-sm btn-edit"
                      onClick={() => handleEdit(activity)}
                      aria-label="Bearbeiten"
                      title="Bearbeiten"
                      style={{ flex: 1 }}
                    >
                      <Pencil size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile Card View */}
          <div className="activities-cards-mobile space-y-3">
            {paginatedActivities.map(activity => (
              <Card key={activity.id} className="p-3" style={{ minHeight: 'auto' }}>
                <div className="space-y-2">
                  <div className="flex gap-2 text-xs" style={{ flexWrap: 'wrap' }}>
                    <span className="font-medium">{activity.name}</span>
                    <span>•</span>
                    <span className="text-gray-600">{activity.age_min}-{activity.age_max} J.</span>
                  </div>

                  <div className="flex gap-2 items-center text-xs pt-2 border-t" style={{ flexWrap: 'wrap' }}>
                    {activity.website ? (
                      <a href={activity.website} target="_blank" rel="noopener noreferrer" className="text-blue-600">
                        Website
                      </a>
                    ) : (
                      <span className="text-gray-500">Kein Link</span>
                    )}
                    <span>•</span>
                    <span className="text-gray-600">{new Date(activity.created_at).toLocaleDateString('de-DE')}</span>
                  </div>

                  <div className="flex gap-2 pt-2 border-t justify-end">
                    <Button
                      variant="outline"
                      onClick={() => handleReject(activity.id)}
                      className="btn-sm btn-reject"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Ablehnen"
                    >
                      <X size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleEdit(activity)}
                      className="btn-sm btn-edit"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Bearbeiten"
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleDelete(activity.id)}
                      className="btn-sm btn-delete-mobile"
                      style={{ padding: 'var(--spacing-1) var(--spacing-2)', flexShrink: 0 }}
                      title="Löschen"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '10px',
              marginTop: '20px',
              padding: '15px 0'
            }}>
              <Button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                variant="outline"
                style={{ padding: '8px 16px', opacity: currentPage === 1 ? 0.5 : 1 }}
              >
                Zurück
              </Button>
              <span style={{ fontSize: '14px', color: '#6c757d' }}>
                Seite {currentPage} von {totalPages} ({total} Einträge)
              </span>
              <Button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                style={{ padding: '8px 16px', opacity: currentPage === totalPages ? 0.5 : 1 }}
              >
                Weiter
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityReview;
